/**
 * BUXAP | bloom — Bundle Deals
 * ---------------------------------------------------------------
 * Single source of truth for the 5 promotional bundles shown on:
 *   • Homepage (5 horizontal carousels)
 *   • /collections/sale  (5 stacked sections)
 *   • Product detail page (bundle banner)
 *   • Cart drawer (progress / "add N more")
 *
 * Each deal targets products whose Shopify variant price equals
 * `unitPrice`. Customers buying `qty` items in that price bracket
 * pay `bundlePrice` instead of `unitPrice × qty`.
 *
 * The actual discount must be configured in Shopify as an
 * **Automatic Buy-X-Get-Y / Bulk discount** for the checkout
 * to honor it. The frontend math here only shows the *display*
 * pricing — it never modifies cart line prices.
 */
import type { ShopifyProduct } from '@/lib/shopify';

export interface BundleDeal {
  /** Stable slug — used as i18n key suffix, URL hash and React key */
  id: string;
  /** Variant price (USD) that defines the group */
  unitPrice: number;
  /** Items required to unlock the bundle */
  qty: number;
  /** Final bundle price (USD) */
  bundlePrice: number;
  /** Storefront search query that returns matching products */
  query: string;
}

export const BUNDLE_DEALS: readonly BundleDeal[] = [
  { id: 'bundle1', unitPrice: 35.95, qty: 3, bundlePrice: 84.99, query: 'variants.price:35.95' },
  { id: 'bundle2', unitPrice: 39.95, qty: 3, bundlePrice: 94.99, query: 'variants.price:39.95' },
  { id: 'bundle3', unitPrice: 42.95, qty: 4, bundlePrice: 134.99, query: 'variants.price:42.95' },
  { id: 'bundle4', unitPrice: 57.95, qty: 3, bundlePrice: 139.99, query: 'variants.price:57.95' },
  { id: 'bundle5', unitPrice: 45.95, qty: 5, bundlePrice: 184.99, query: 'variants.price:45.95' },
  { id: 'bundle6', unitPrice: 33.95, qty: 4, bundlePrice: 99.99, query: 'variants.price:33.95' },
  { id: 'bundle7', unitPrice: 49.95, qty: 3, bundlePrice: 119.99, query: 'variants.price:49.95' },
  { id: 'bundle8', unitPrice: 61.95, qty: 3, bundlePrice: 148.99, query: 'variants.price:61.95' },
  { id: 'bundle9', unitPrice: 65.95, qty: 2, bundlePrice: 99.99, query: 'variants.price:65.95' },
  { id: 'bundle10', unitPrice: 74.95, qty: 3, bundlePrice: 179.99, query: 'variants.price:74.95' },
] as const;

/** Tolerance for matching a product price to a bundle's unitPrice (cents). */
const PRICE_EPSILON = 0.005;

/** Find the bundle that a product belongs to (or undefined). */
export function getBundleForProduct(product: ShopifyProduct): BundleDeal | undefined {
  const amount = parseFloat(product.priceRange?.minVariantPrice?.amount ?? '0');
  if (!Number.isFinite(amount)) return undefined;
  return BUNDLE_DEALS.find((b) => Math.abs(b.unitPrice - amount) < PRICE_EPSILON);
}

/** Same but takes the numeric price directly. */
export function getBundleForPrice(price: number): BundleDeal | undefined {
  if (!Number.isFinite(price)) return undefined;
  return BUNDLE_DEALS.find((b) => Math.abs(b.unitPrice - price) < PRICE_EPSILON);
}

/** Look up a deal by id. */
export function getBundleById(id: string): BundleDeal | undefined {
  return BUNDLE_DEALS.find((b) => b.id === id);
}

/**
 * Calculate the total bundle discount for a given set of cart line items.
 * Mirrors Shopify's automatic "Amount off products — per each eligible item,
 * minimum quantity N" rules: every full group of `qty` items at `unitPrice`
 * gets the bundle discount applied.
 */
export function calcBundleDiscount(
  items: ReadonlyArray<{ price: number; quantity: number }>,
): number {
  let discount = 0;
  for (const deal of BUNDLE_DEALS) {
    const matchingQty = items
      .filter((i) => Math.abs(i.price - deal.unitPrice) < PRICE_EPSILON)
      .reduce((sum, i) => sum + i.quantity, 0);
    const bundles = Math.floor(matchingQty / deal.qty);
    if (bundles > 0) {
      const perBundleSaving = deal.unitPrice * deal.qty - deal.bundlePrice;
      discount += bundles * perBundleSaving;
    }
  }
  return discount;
}
